import { apiRequest } from '../api/client';
import { fetchAllPages } from '../api/fetch-all-pages';

export const ZONES = ['RIO_NEGRO', 'CALAFATE', 'VACA_MUERTA'] as const;
export type Zone = (typeof ZONES)[number];

export const ZONE_LABELS: Record<Zone, string> = {
  RIO_NEGRO: 'Río Negro',
  CALAFATE: 'El Calafate',
  VACA_MUERTA: 'Vaca Muerta',
};

export const CUSTOMER_STATUSES = ['ACTIVE', 'SUSPENDED'] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const TAX_CONDITIONS = [
  'RESPONSABLE_INSCRIPTO',
  'MONOTRIBUTO',
  'EXENTO',
  'CONSUMIDOR_FINAL',
] as const;
export type TaxCondition = (typeof TAX_CONDITIONS)[number];

export const TAX_CONDITION_LABELS: Record<TaxCondition, string> = {
  RESPONSABLE_INSCRIPTO: 'Responsable Inscripto',
  MONOTRIBUTO: 'Monotributo',
  EXENTO: 'Exento',
  CONSUMIDOR_FINAL: 'Consumidor Final',
};

export interface CustomerAddress {
  street: string;
  number: string;
  city: string;
  province: string;
  postalCode: string;
}

export interface CustomerListItem {
  id: string;
  legalName: string;
  tradeName: string;
  taxId: string;
  zone: Zone;
  /** `INACTIVE` exists in the API's `CustomerStatus` enum but is unused by
   * any current flow (deletion is a soft delete via `deletedAt`, not a
   * status transition); kept here for type accuracy against the contract. */
  status: CustomerStatus | 'INACTIVE';
}

export interface CustomerSearchResult {
  items: CustomerListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CustomerSearchParams {
  q: string | undefined;
  zone: Zone | undefined;
  status: CustomerStatus | undefined;
  page: number;
  pageSize: number;
}

/** The largest page `GET /customers` accepts; anything above is a 400. */
export const MAX_CUSTOMER_PAGE_SIZE = 100;

/** Matches `apps/api` `GET /customers` (`customer-search.dto.ts`) exactly. */
export function searchCustomers(params: CustomerSearchParams): Promise<CustomerSearchResult> {
  const query = new URLSearchParams();

  if (params.q !== undefined && params.q !== '') {
    query.set('q', params.q);
  }
  if (params.zone !== undefined) {
    query.set('zone', params.zone);
  }
  if (params.status !== undefined) {
    query.set('status', params.status);
  }
  query.set('page', String(params.page));
  query.set('pageSize', String(params.pageSize));

  return apiRequest<CustomerSearchResult>(`/customers?${query.toString()}`);
}

/**
 * Every customer matching `filters`, walking `GET /customers` past its
 * `MAX_CUSTOMER_PAGE_SIZE` cap (`fetchAllPages`) instead of the single page a
 * picker used to request — the gap that let a customer past #100 never show
 * up in a select.
 */
export function searchAllCustomers(
  filters: Omit<CustomerSearchParams, 'page' | 'pageSize'>,
): Promise<CustomerSearchResult> {
  return fetchAllPages<CustomerListItem>(
    (page, pageSize) => searchCustomers({ ...filters, page, pageSize }),
    MAX_CUSTOMER_PAGE_SIZE,
  ).then((items) => ({ items, page: 1, pageSize: MAX_CUSTOMER_PAGE_SIZE, total: items.length }));
}

export interface CustomerDetail {
  id: string;
  legalName: string;
  tradeName: string;
  taxId: string;
  taxCondition: TaxCondition;
  billingEmail: string;
  billingAddress: CustomerAddress | null;
  zone: Zone;
  status: CustomerStatus | 'INACTIVE';
  notes: string | null;
  suspendedAt: string | null;
  suspensionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerFormInput {
  legalName: string;
  tradeName: string;
  taxId: string;
  taxCondition: TaxCondition;
  billingEmail: string;
  billingAddress?: CustomerAddress;
  zone: Zone;
  notes?: string;
}

/** Matches `apps/api` `GET /customers/:id` (`get-customer-detail.use-case.ts`). */
export function getCustomer(id: string): Promise<CustomerDetail> {
  return apiRequest<CustomerDetail>(`/customers/${id}`);
}

/** Matches `apps/api` `POST /customers` (`customer.dto.ts` → `createCustomerSchema`). */
export function createCustomer(input: CustomerFormInput): Promise<CustomerDetail> {
  return apiRequest<CustomerDetail>('/customers', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `PATCH /customers/:id`; `input` is only the changed fields. */
export function updateCustomer(
  id: string,
  input: Partial<CustomerFormInput>,
): Promise<CustomerDetail> {
  return apiRequest<CustomerDetail>(`/customers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `POST /customers/:id/suspend`. */
export function suspendCustomer(id: string, reason: string): Promise<CustomerDetail> {
  return apiRequest<CustomerDetail>(`/customers/${id}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** Matches `apps/api` `POST /customers/:id/reactivate`. */
export function reactivateCustomer(id: string): Promise<CustomerDetail> {
  return apiRequest<CustomerDetail>(`/customers/${id}/reactivate`, { method: 'POST' });
}

/** Matches `apps/api` `DELETE /customers/:id` (soft delete, 204 No Content). */
export function deleteCustomer(id: string): Promise<void> {
  return apiRequest<void>(`/customers/${id}`, { method: 'DELETE' });
}

/** Matches `apps/api` `StoredSite` (`customer-site.repository.ts`) response shape. */
export interface Site {
  id: string;
  customerId: string;
  name: string;
  address: CustomerAddress | null;
  latitude: number | null;
  longitude: number | null;
  /** "HH:mm", both present or both null. */
  deliveryWindowStart: string | null;
  deliveryWindowEnd: string | null;
  receptionInstructions: string | null;
  defaultReceiverName: string | null;
  contactPhone: string | null;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteFormInput {
  name: string;
  address?: CustomerAddress;
  latitude?: number;
  longitude?: number;
  deliveryWindowStart?: string;
  deliveryWindowEnd?: string;
  receptionInstructions?: string;
  defaultReceiverName?: string;
  contactPhone?: string;
}

/** Matches `apps/api` `GET /customers/:customerId/sites`, default first (`design.md` §6.1). */
export function listSites(customerId: string): Promise<Site[]> {
  return apiRequest<Site[]>(`/customers/${customerId}/sites`);
}

/** Matches `apps/api` `POST /customers/:customerId/sites` (`createSiteSchema`). */
export function createSite(customerId: string, input: SiteFormInput): Promise<Site> {
  return apiRequest<Site>(`/customers/${customerId}/sites`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface SiteUpdateInput {
  name?: string;
  address?: CustomerAddress;
  latitude?: number;
  longitude?: number;
  deliveryWindowStart?: string | null;
  deliveryWindowEnd?: string | null;
  receptionInstructions?: string;
  defaultReceiverName?: string;
  contactPhone?: string;
  isActive?: boolean;
}

/** Matches `apps/api` `PATCH /customers/:customerId/sites/:siteId`; `input` is only the changed fields. */
export function updateSite(
  customerId: string,
  siteId: string,
  input: SiteUpdateInput,
): Promise<Site> {
  return apiRequest<Site>(`/customers/${customerId}/sites/${siteId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `DELETE /customers/:customerId/sites/:siteId` (soft delete, 204 No Content). */
export function deleteSite(customerId: string, siteId: string): Promise<void> {
  return apiRequest<void>(`/customers/${customerId}/sites/${siteId}`, { method: 'DELETE' });
}

/** Matches `apps/api` `POST /customers/:customerId/sites/:siteId/set-default`. */
export function setDefaultSite(customerId: string, siteId: string): Promise<Site> {
  return apiRequest<Site>(`/customers/${customerId}/sites/${siteId}/set-default`, {
    method: 'POST',
  });
}

export const CONTACT_ROLES = ['PURCHASING', 'RECEPTION', 'BILLING', 'MANAGEMENT'] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export const CONTACT_ROLE_LABELS: Record<ContactRole, string> = {
  PURCHASING: 'Compras',
  RECEPTION: 'Recepción',
  BILLING: 'Facturación',
  MANAGEMENT: 'Dirección',
};

/** Matches `apps/api` `toStored` (`customer-contact.prisma-repository.ts`) response shape; `userId` never travels, only `hasPortalUser`. */
export interface Contact {
  id: string;
  customerId: string;
  siteId: string | null;
  fullName: string;
  role: ContactRole;
  email: string | null;
  phone: string | null;
  receivesNotifications: boolean;
  hasPortalUser: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ContactFormInput {
  fullName: string;
  role: ContactRole;
  email?: string;
  phone?: string;
  receivesNotifications: boolean;
  siteId?: string | null;
}

export interface ContactUpdateInput {
  fullName?: string;
  role?: ContactRole;
  email?: string;
  phone?: string;
  receivesNotifications?: boolean;
  siteId?: string | null;
}

/** Matches `apps/api` `GET /customers/:customerId/contacts`, corporate first (`design.md` §6.2, decision 4). */
export function listContacts(customerId: string): Promise<Contact[]> {
  return apiRequest<Contact[]>(`/customers/${customerId}/contacts`);
}

/** Matches `apps/api` `POST /customers/:customerId/contacts` (`createContactSchema`). */
export function createContact(customerId: string, input: ContactFormInput): Promise<Contact> {
  return apiRequest<Contact>(`/customers/${customerId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `PATCH /customers/:customerId/contacts/:contactId`; `input` is only the changed fields. */
export function updateContact(
  customerId: string,
  contactId: string,
  input: ContactUpdateInput,
): Promise<Contact> {
  return apiRequest<Contact>(`/customers/${customerId}/contacts/${contactId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `DELETE /customers/:customerId/contacts/:contactId` (soft delete, 204 No Content). */
export function deleteContact(customerId: string, contactId: string): Promise<void> {
  return apiRequest<void>(`/customers/${customerId}/contacts/${contactId}`, { method: 'DELETE' });
}
